import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, Check } from "lucide-react";
import { RevealLines, Tilt, EASE } from "../components/site/motion";
import { joinWaitlist, errorMessage } from "../lib/api";
import { M } from "../data/site";

const INTERESTS = ["Kitchen Companion", "HonestGrow™ University", "Little Chefs Academy", "Family pilot program"];
const SIZES = ["1–2", "3–4", "5–6", "7+"];
const inputCls = "w-full rounded-xl border border-cyan-400/25 bg-deep/70 px-4 py-3.5 text-white placeholder:text-slate-400 focus:border-cyan-300 focus:outline-none";

export default function Waitlist() {
  const [form, setForm] = useState({ name: "", email: "", household_size: "3–4", note: "" });
  const [interests, setInterests] = useState(["Kitchen Companion"]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await joinWaitlist({ ...form, note: form.note || null, interests });
      setDone(r);
      toast.success("You’re on the waitlist!");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="waitlist-page" className="relative overflow-hidden">
      <div className="grid-lines absolute inset-0 opacity-50" />
      <section className="relative mx-auto grid max-w-[1440px] gap-12 px-5 pb-24 pt-28 lg:grid-cols-12 lg:px-12 lg:pt-40">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-6">Join the waitlist</p>
          <RevealLines className="font-display text-5xl font-bold leading-[0.98] tracking-tight text-white sm:text-6xl" lines={[{ text: "Welcome Honesti" }, { text: "into your home.", className: "text-grad" }]} />
          <p className="mt-6 text-base text-slate-200 md:text-lg">Be among the first families to hear about pilots, early access and new learning programs.</p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: EASE, delay: 0.3 }} className="mt-10 hidden lg:block">
            <Tilt max={6}><div className="aspect-[4/5] max-w-sm overflow-hidden rounded-[2rem] glow-edge"><img src={M("waitlist.jpg")} alt="Honesti welcoming you" className="h-full w-full object-cover" /></div></Tilt>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.2 }} className="lg:col-span-6 lg:col-start-7">
          {done ? (
            <div data-testid="waitlist-success" className="glass rounded-[2rem] p-8 lg:p-12">
              <CheckCircle2 className="h-14 w-14 text-cyan-300" />
              <h2 className="mt-6 font-display text-4xl font-bold text-white">You’re on the list, {done.name.split(" ")[0]}.</h2>
              <p className="mt-4 text-slate-200">We saved <span className="text-white">{done.email}</span> to the HonestChoice AI waitlist. A welcome note from Honesti is on its way to your inbox.</p>
              <Link to="/features" data-testid="waitlist-explore-btn" className="btn-ghost mt-8">Keep exploring <ArrowRight className="h-4 w-4" /></Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="waitlist-form" className="glass space-y-6 rounded-[2rem] p-6 sm:p-8 lg:p-12">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block"><span className="eyebrow mb-2 block">Your name</span><input required maxLength={120} value={form.name} onChange={set("name")} data-testid="waitlist-name-input" placeholder="Jordan Lee" className={inputCls} /></label>
                <label className="block"><span className="eyebrow mb-2 block">Email</span><input required type="email" value={form.email} onChange={set("email")} data-testid="waitlist-email-input" placeholder="you@example.com" className={inputCls} /></label>
              </div>
              <div>
                <span className="eyebrow mb-3 block">Household size</span>
                <div className="flex flex-wrap gap-2">{SIZES.map((s) => <button type="button" key={s} onClick={() => setForm((f) => ({ ...f, household_size: s }))} data-testid={`waitlist-size-${s}`} className={`rounded-full border px-5 py-2 text-sm transition-colors ${form.household_size === s ? "border-cyan-300 bg-cyan-400/15 text-white" : "border-cyan-400/25 text-slate-300"}`}>{s}</button>)}</div>
              </div>
              <div>
                <span className="eyebrow mb-3 block">I’m most interested in</span>
                <div className="grid gap-2 sm:grid-cols-2">
                  {INTERESTS.map((i) => {
                    const on = interests.includes(i);
                    return (
                      <button type="button" key={i} onClick={() => setInterests((l) => (on ? l.filter((x) => x !== i) : [...l, i]))} data-testid={`waitlist-interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors ${on ? "border-cyan-300 bg-cyan-400/10 text-white" : "border-cyan-400/20 text-slate-300"}`}>
                        <span className={`grid h-5 w-5 place-items-center rounded-md border ${on ? "border-cyan-300 bg-cyan-400 text-ink" : "border-cyan-400/40"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>{i}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="block"><span className="eyebrow mb-2 block">Anything we should know? (optional)</span><textarea maxLength={500} rows={3} value={form.note} onChange={set("note")} data-testid="waitlist-note-input" className={inputCls} /></label>
              <button disabled={busy} data-testid="waitlist-submit-btn" className="btn-primary w-full !py-4 text-lg disabled:opacity-60">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Join the Waitlist <ArrowRight className="h-5 w-5" /></>}</button>
              <p className="text-xs text-slate-300">Your details are saved to our waitlist, and Honesti will send you a welcome email. We only use them to contact you about HonestChoice AI.</p>
            </form>
          )}
        </motion.div>
      </section>
    </div>
  );
}
